export function calculateDirectionBetweenPoints(x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.atan2(dy, dx);
}

export function getDistanceBetweenPoints(x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx ** 2 + dy ** 2);
}

export function incrementDistanceToAPointBasedOnADirection(x: number, y: number, radDirection: number, increment = 10) {
  const dx = Math.cos(radDirection) * increment;
  const dy = Math.sin(radDirection) * increment;
  const newX = x + dx;
  const newY = y + dy;
  return { x: newX, y: newY };
}

export function getEdgePositionPayload(
  fatherNode: any,
  bridgeNode: any,
  childNodes: any[],
  desiredEdgeLength = 50,
  expandPiMultiplier = 2,
  getNodes?: any,
) {
  const nodePayload: any = {};
  const piMultiplier = expandPiMultiplier;
  let childNodeSpacingIncrement = (Math.PI * piMultiplier) / childNodes.length;
  if (piMultiplier <= 1) {
    childNodeSpacingIncrement = childNodeSpacingIncrement > Math.PI / 4 ? Math.PI / 4 : childNodeSpacingIncrement;
  }
  const currentIncrementValue = childNodeSpacingIncrement;
  const { x: accountXTmp, y: accountYTmp } = fatherNode.position || { x: 0, y: 0 };
  const { height: fatherHeight = 0, width: fatherWidth = 0 } = fatherNode;
  const { x: bridgeXTmp, y: bridgeYTmp } = bridgeNode.position || { x: 0, y: 0 };
  const { height: bridgeHeight = 0, width: bridgeWidth = 0 } = bridgeNode;

  const accountX = accountXTmp + fatherWidth / 4;
  const accountY = accountYTmp + fatherHeight / 4;
  const bridgeX = bridgeXTmp + bridgeWidth / 4;
  const bridgeY = bridgeYTmp + bridgeHeight / 4;

  const pivotDirection = calculateDirectionBetweenPoints(accountX, accountY, bridgeX, bridgeY);
  getNodeCircleLayout({
    childNodes,
    pivotDirection,
    desiredEdgeLength,
    bridgeX,
    bridgeY,
    currentIncrementValue,
    childNodeSpacingIncrement,
    nodePayload,
    layerLevelThreshold: 8 * piMultiplier,
  });
  const allNodes = getNodes ? getNodes() : [];
  checkIntersections(nodePayload, allNodes, desiredEdgeLength);
  return nodePayload;
}

const getMinDistance = (type: string) => {
  if(type === 'RecordNode') return 30;
  if(type === 'BridgeNode') return 60;
  if(type === 'AccountNode') return 120;
  return 18
}

const checkIntersections = (nodePayload: any, allNodes: any[], desiredEdgeLength: number) => {
  let intersect: any[] = [];
  const allNodesIncludingNew = [
    ...allNodes.map((singleNode) => ({ ...singleNode.position, id: singleNode.id })),
    ...Object.keys(nodePayload).map((key) => ({ ...nodePayload[key], id: key })),
  ];
  Object.keys(nodePayload).forEach((singleKey) => {
    const newObj = nodePayload[singleKey];
    const intersecting = allNodesIncludingNew.filter((oldObj: any) => {
      const minDistance = getMinDistance(oldObj.type);
      return (
        oldObj.id !== singleKey && getDistanceBetweenPoints(newObj?.x, newObj?.y, oldObj?.x, oldObj?.y) < minDistance
      );
    });
    if (intersecting.length) {
      intersect = [...intersect, singleKey];
    }
  });

  intersect.forEach((key: string) => {
    const maxIterations = 200;
    let i = 0;
    while (i < maxIterations) {
      i = i + 1;
      const { x, y, dir } = generateNewPosition(nodePayload[key], desiredEdgeLength, nodePayload);
      if (
        !allNodesIncludingNew.some((oldObj: any) => {
          const minDistance = getMinDistance(oldObj.type);
          return oldObj.id !== key && getDistanceBetweenPoints(x, y, oldObj?.x, oldObj?.y) < minDistance;
        })
      ) {
        nodePayload[key] = { x, y, dir };
        break;
      }
    }
  });
};

const generateNewPosition = (
  position: { x: number; y: number; dir: number },
  desiredEdgeLength: number,
  nodePayload: any,
) => {
  const { x, y } = position;
  const minDir = (0.9 + Math.random() * 0.2) * Math.min(...Object.values(nodePayload).map((entry: any) => entry.dir));
  const maxDir = (0.9 + Math.random() * 0.2) * Math.max(...Object.values(nodePayload).map((entry: any) => entry.dir));

  const newLength = desiredEdgeLength * (0.2 + Math.random() * 2);
  const newDir = minDir + Math.random() * (maxDir - minDir);
  const dx = Math.cos(newDir) * newLength;
  const dy = Math.sin(newDir) * newLength;

  return {
    dir: newDir,
    x: x + dx,
    y: y + dy,
  };
};

const getNodeCircleIntercalatedLayout = ({
  childNodes,
  pivotDirection,
  desiredEdgeLength,
  bridgeX,
  bridgeY,
  currentIncrementValue,
  childNodeSpacingIncrement,
  nodePayload,
  layerLevelThreshold,
}: any) => {
  let newCurrentIncrementValue = currentIncrementValue;
  let layerLevels = 1;
  let verticalDiff = 0;
  let initialPositionPivot = 1;
  if (childNodes.length > layerLevelThreshold) {
    layerLevels = Math.floor(childNodes.length / layerLevelThreshold) + 1;
    verticalDiff = desiredEdgeLength / layerLevels;
  }

  const middleIndex = Math.floor(childNodes.length / 2);
  if (childNodes.length > 0) {
    const dir = pivotDirection;
    const dx = Math.cos(dir) * desiredEdgeLength;
    const dy = Math.sin(dir) * desiredEdgeLength;
    const newX = bridgeX + dx;
    const newY = bridgeY + dy;
    nodePayload[childNodes[middleIndex]] = { x: newX, y: newY, dir };
  }

  //Process from the middle node down
  for (let i = middleIndex - 1; i >= 0; i--) {
    let dx = 0;
    let dy = 0;
    const dir = pivotDirection - newCurrentIncrementValue;
    dx = Math.cos(dir) * (desiredEdgeLength - verticalDiff * (initialPositionPivot % layerLevels));
    dy = Math.sin(dir) * (desiredEdgeLength - verticalDiff * (initialPositionPivot % layerLevels));
    const newX = bridgeX + dx;
    const newY = bridgeY + dy;
    nodePayload[childNodes[i]] = { x: newX, y: newY, dir };
    newCurrentIncrementValue += childNodeSpacingIncrement;
    initialPositionPivot++;
  }

  newCurrentIncrementValue = childNodeSpacingIncrement;
  initialPositionPivot = 1;

  for (let i = middleIndex + 1; i < childNodes.length; i++) {
    let dx = 0;
    let dy = 0;
    const dir = pivotDirection + newCurrentIncrementValue;
    dx = Math.cos(dir) * (desiredEdgeLength - verticalDiff * (initialPositionPivot % layerLevels));
    dy = Math.sin(dir) * (desiredEdgeLength - verticalDiff * (initialPositionPivot % layerLevels));
    const newX = bridgeX + dx;
    const newY = bridgeY + dy;
    nodePayload[childNodes[i]] = { x: newX, y: newY, dir };
    newCurrentIncrementValue += childNodeSpacingIncrement;
    initialPositionPivot++;
  }
};

const getNodeCircleLayout = ({
  childNodes,
  pivotDirection,
  desiredEdgeLength,
  bridgeX,
  bridgeY,
  currentIncrementValue,
  childNodeSpacingIncrement,
  nodePayload,
}: any) => {
  let newCurrentIncrementValue = currentIncrementValue;
  const middleIndex = childNodes.length % 2 ? Math.floor(childNodes.length / 2) : childNodes.length / 2 - 1;

  if (childNodes.length > 0) {
    const dir = childNodes.length % 2 ? pivotDirection : pivotDirection - currentIncrementValue / 2;
    const dx = Math.cos(dir) * (desiredEdgeLength + ((middleIndex % 2) / 3) * desiredEdgeLength);
    const dy = Math.sin(dir) * (desiredEdgeLength + ((middleIndex % 2) / 3) * desiredEdgeLength);
    const newX = bridgeX + dx;
    const newY = bridgeY + dy;
    nodePayload[childNodes[middleIndex]] = { x: newX, y: newY, dir };
  }

  //Process from the middle node down
  for (let i = middleIndex - 1; i >= 0; i--) {
    let dx = 0;
    let dy = 0;
    const dir = pivotDirection - (childNodes.length % 2 ? 0 : currentIncrementValue / 2) - newCurrentIncrementValue;
    dx = Math.cos(dir) * (desiredEdgeLength + ((i % 2) / 3) * desiredEdgeLength);
    dy = Math.sin(dir) * (desiredEdgeLength + ((i % 2) / 3) * desiredEdgeLength);
    const newX = bridgeX + dx;
    const newY = bridgeY + dy;
    nodePayload[childNodes[i]] = { x: newX, y: newY, dir };
    newCurrentIncrementValue += childNodeSpacingIncrement;
  }

  newCurrentIncrementValue = childNodeSpacingIncrement;

  for (let i = middleIndex + 1; i < childNodes.length; i++) {
    let dx = 0;
    let dy = 0;
    const dir = pivotDirection - (childNodes.length % 2 ? 0 : currentIncrementValue / 2) + newCurrentIncrementValue;
    dx = Math.cos(dir) * (desiredEdgeLength + ((i % 2) / 3) * desiredEdgeLength);
    dy = Math.sin(dir) * (desiredEdgeLength + ((i % 2) / 3) * desiredEdgeLength);
    const newX = bridgeX + dx;
    const newY = bridgeY + dy;
    nodePayload[childNodes[i]] = { x: newX, y: newY, dir };
    newCurrentIncrementValue += childNodeSpacingIncrement;
  }
};
